// The chat panel drives the browser-local agent loop: it builds the provider
// from the account panel, runs runTurn directly, executes each tool request
// through requestTool, and renders streamed text. Provider HTTP targets the
// relay, which proxies path-preserving to the provider and stores nothing.
import { buildMessages, buildSystemPrompt, createProvider, DEFAULT_API, effectiveHistoryBudget, partitionTranscript, runTurn as defaultRunTurn, SUMMARY_PREFIX, summarizeTranscript, summarizeWithProvider, toolDetail, turnsOf } from '@jscadui/agent-loop'
import { readOverride, readVisionCache, resolveCatalogVision } from './modelVision.js'
import { checkTurn, FREE_NOTICE_MESSAGE, getRecordOwnChats, isFreeSelection, openConsentGate, TOS_REQUIRED_MESSAGE } from './aiConsent.js'
import { imageFiles, MAX_CHAT_IMAGES, prepareImage as defaultPrepareImage } from './chatImages.js'

/* global __RELAY_ORIGIN__ */
const RELAY_OVERRIDE_KEY = 'jscad-ai.relay'

// build.js stamps __RELAY_ORIGIN__; unit tests run unbundled without it.
const relayDefault = () => (typeof __RELAY_ORIGIN__ === 'string' ? __RELAY_ORIGIN__ : 'https://jscad.rkroll.com')

const relayRoot = () => (globalThis.localStorage?.getItem(RELAY_OVERRIDE_KEY) || relayDefault()).replace(/\/+$/, '')

// A free (jscad-web) selection rides the one relay endpoint family at
// `<root>/api/relay/jscad-web/<id>`, where the relay injects the server-held
// key; own keys ride their kind's route.
export const relayBaseUrl = (selection) => {
  const sel = selection ?? {}
  if (isFreeSelection(sel)) return `${relayRoot()}/api/relay/jscad-web/${sel.inference}`
  return `${relayRoot()}/api/relay/${sel.kind}`
}

// The free option catalog the relay serves beside the per-id turn routes.
export const inferenceListUrl = () => `${relayRoot()}/api/relay/jscad-web/v1/models`

// Whether the selected model reads images, so the loop may offer it `view`.
// Three sources, in order: a manual per-model override (the only source that
// can name a model whose provider serves no capability metadata), the
// provider's own `capabilities.vision`, then the cached models.dev prune.
// Anything else leaves the image tool off, so a text-only model is never
// handed pixels it cannot read. There is no hardcoded table: a static list
// lags every catalog rotation, so live data decides.
export const visionCapable = (selection) => {
  if (selection == null || typeof selection.model !== 'string' || selection.model === '') return false
  if (readOverride(globalThis.localStorage, selection.model) === true) return true
  if (selection.capabilities?.vision === true) return true
  const cached = readVisionCache(globalThis.localStorage)
  return resolveCatalogVision(cached && cached.providers, selection.kind, selection.model) === true
}

// The loop needs a `view` result whole: its renders ride beside the text as
// image parts, which only an object survives. Every other result travels as
// JSON text.
const forLoop = (result) =>
  result !== null && typeof result === 'object' && Array.isArray(result.shots) ? result : typeof result === 'string' ? result : JSON.stringify(result ?? null)

// The transcript shows a `view` result's cameras, never its pixels: the data
// URLs would swamp the tool row and never belong in the DOM.
const forDisplay = (result) =>
  result !== null && typeof result === 'object' && Array.isArray(result.shots)
    ? JSON.stringify({ ok: result.ok ?? true, cameras: result.shots.map((shot) => shot.camera) })
    : result

// One line above the input while our recording is on, and the free-model
// variant while a free selection is active (free turns cannot be opted out).
export const CHAT_LOG_NOTICE = 'Chats are saved to improve the assistant. Turn this off in Chat settings.'
export const CHAT_FREE_NOTICE = 'Free jscad-web models share conversations to improve the assistant.'
// The relay's 402 when a free-model budget is spent; the turn never reached a model.
export const INFERENCE_QUOTA_MESSAGE = 'This free model is out of budget right now. Add your own key in Chat settings to keep chatting.'

const inferenceQuotaError = (err) =>
  err?.status === 402 && /inference_quota_exhausted/.test(`${err?.message ?? ''} ${err?.body ?? ''}`)

// The relay's JSON body limit, `RELAY_JSON_LIMIT` ('1mb') in
// server/src/relay/body.ts, which body-parser reads as 1024 * 1024 bytes.
const RELAY_BODY_LIMIT_BYTES = 1024 * 1024

export const REQUEST_TOO_LARGE_MESSAGE = 'This message is too large for the relay, which takes 1 MB. Send fewer images or start a new chat.'
const requestTooLarge = (err) => err?.name === 'RequestTooLargeError' || err?.status === 413

export const NO_VISION_TITLE = 'The selected model does not read images. Pick a vision model in Chat settings to attach one.'
export const NO_VISION_NOTE = 'The selected model does not read images, so nothing was attached.'
export const NO_VISION_SEND = 'The selected model does not read images. Remove the images or pick a vision model in Chat settings.'
export const IMAGE_CAP_NOTE = `Up to ${MAX_CHAT_IMAGES} images per message.`

// The stored conversation keeps the bytes of this many newest image messages;
// older ones keep only their type and size, so storage stays bounded.
export const STORED_IMAGE_MESSAGES = 4

const hasImages = (m) => Array.isArray(m?.images) && m.images.length > 0

const forStorage = (messages) => {
  const keep = new Set(messages.filter((m) => m.role === 'user' && hasImages(m)).slice(-STORED_IMAGE_MESSAGES))
  return messages.map((m) => (m.role !== 'user' || !hasImages(m) || keep.has(m) ? m : { ...m, images: m.images.map(({ dataUrl: _dataUrl, ...meta }) => meta) }))
}

const el = (tag, className, text) => {
  const node = document.createElement(tag)
  node.className = className
  if (text !== undefined) node.textContent = text
  return node
}

const STOPPED_NOTE = '[stopped by the user]'

// A question the turn is awaiting is settled with this when Stop aborts the
// turn, so `handleTool` reports a tool error instead of hanging.
const abortError = (message = 'turn aborted') => Object.assign(new Error(message), { name: 'AbortError' })

// The selection stores 'off'; callers may also pass false. Both remove the tool.
const websearchOff = (provider) => provider === false || provider === 'off'

// Blanket web permission: granted once per conversation, not per call, and
// shared by websearch and webfetch. The first ungranted call asks the user
// with a question card, so the grant comes from the user, never from the
// model's say-so. Every granted call is logged so spend is auditable.
export const WEB_ALLOW = 'Allow for this conversation'
export const WEB_DECLINE = 'Not now'
export const WEB_PERMISSION_QUESTION = {
  header: 'Web access',
  question: 'The assistant wants to search the web or fetch a page. Allow web access for this conversation?',
  options: [WEB_ALLOW, WEB_DECLINE],
}
const ALLOWS = /^\s*(yes|y|ok|okay|sure|allow)\b/i

export const webDeclined = (name) => `${name}: the user declined web access for this conversation; carry on without it`

// No card could be shown (no chat UI, or the turn ended).
export const webUnasked = (name) => `${name}: web access is not granted for this conversation; the user can allow it in Chat settings`

export const createWebsearchGrant = ({ granted = false, log = () => {}, ask } = {}) => {
  let on = granted
  let declined = false
  // Parallel web calls share one card.
  let asking = null
  const askOnce = () => {
    asking ??= Promise.resolve()
      .then(() => ask(WEB_PERMISSION_QUESTION))
      .then(
        (answer) => answer === WEB_ALLOW || (answer !== WEB_DECLINE && ALLOWS.test(String(answer))),
        () => null,
      )
      .finally(() => {
        asking = null
      })
    return asking
  }
  return {
    isGranted: () => on,
    setGranted: (next) => {
      on = next === true
      declined = false
    },
    beforeToolCall: async (name) => {
      if (on || (name !== 'websearch' && name !== 'webfetch')) return undefined
      if (declined) return { decision: 'deny', reason: webDeclined(name) }
      const allowed = ask ? await askOnce() : null
      if (allowed === true) {
        on = true
        return undefined
      }
      if (allowed === false) {
        declined = true
        return { decision: 'deny', reason: webDeclined(name) }
      }
      return { decision: 'deny', reason: webUnasked(name) }
    },
    afterToolCall: async (name, input, result) => {
      if (!on || (name !== 'websearch' && name !== 'webfetch')) return undefined
      let parsed = result
      if (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed)
        } catch {
          return undefined
        }
      }
      if (!parsed || typeof parsed !== 'object') return undefined
      // Audit logging must never corrupt a good result.
      try {
        if (name === 'webfetch') {
          if (parsed.ok === true) {
            log({ url: input?.url, bytes: typeof parsed.content === 'string' ? parsed.content.length : 0 })
          }
          return undefined
        }
        if (!Array.isArray(parsed.results)) return undefined
        const providers = [...new Set(parsed.results.map((hit) => hit?.provider).filter(Boolean))]
        log({ query: input?.query, provider: providers.join('+') || 'unknown', count: parsed.results.length })
      } catch {
        // The transcript already carries the tagged results.
      }
      return undefined
    },
  }
}

// A stopped reply may be empty, and providers refuse an empty assistant message.
// Stored reasoning stays on the page: the model never gets it back. A reply's
// mentions come through, since `buildMessages` reads their paths for recency.
// A message that only carries an undo has nothing the model should see.
const forModelOf = (msgs) => msgs.filter((m) => m.role !== 'assistant' || m.content || m.stopped).map(forModel)
const forModel = (m) => {
  const base = { role: m.role, content: m.content }
  if (m.stopped) base.content = m.content ? `${m.content}\n\n${STOPPED_NOTE}` : STOPPED_NOTE
  if (m.role === 'assistant' && m.mentions?.length > 0) base.mentions = m.mentions
  if (m.role === 'user' && hasImages(m)) base.images = m.images
  return base
}

// A summary rides at the front of the transcript as a system message; `basis`
// counts the leading model-coordinate messages already folded into it.
const isSummaryMsg = (m) => m?.role === 'system' && typeof m?.basis === 'number'
const stripSummary = (msgs) => msgs.filter((m) => !isSummaryMsg(m))

const thinkingLabel = (seconds) => `Thinking… ${seconds}s`
const thoughtLabel = (seconds) => `Thought for ${seconds}s`

// A tool answers failure as {ok:false}, as an object or as its JSON text.
const toolFailed = (result) => {
  if (typeof result === 'string') {
    try {
      result = JSON.parse(result)
    } catch {
      return false
    }
  }
  return result !== null && typeof result === 'object' && result.ok === false
}

const phaseLabel = (status) => {
  switch (status.phase) {
    case 'text':
      return 'Writing…'
    case 'tool':
      return `${status.tool}${status.detail ? ` ${status.detail}` : ''}…`
    case 'retry':
      return `Provider busy, retrying (${status.attempt}/${status.maxAttempts})…`
    default:
      return 'Thinking…'
  }
}

// Providers stream reasoning text, not its token count; about four characters make a token.
const CHARS_PER_TOKEN = 4

/**
 * `getBuild` answers the open project's last build report, whichever run made
 * it; `startTurn` runs before a turn reads the project for its context, and
 * `endTurn` once after every turn, when its writes are final, with the turn's
 * user text so its chat version can carry a short excerpt.
 * `getSummarizer` picks the model that summarizes evicted turns; it defaults
 * to `getProvider`, so wiring a cheaper model is a selection swap, not a
 * change here.
 * @param {{container:HTMLElement,requestTool:Function,getProvider:Function,getSummarizer?:Function,getApi?:()=>'fluent'|'modeling',runTurnFn?:Function,beforeToolCall?:Function,afterToolCall?:Function,storage?:{readConversation:Function,writeConversation:Function},projectId?:string|(()=>string),getProjectFiles?:()=>Promise<Record<string,string|ArrayBuffer>>,getBuild?:()=>Promise<object|null>,startTurn?:()=>void,endTurn?:(excerpt?:string)=>Promise<Array<{projectId:string,versionId:string|null}>|void>,restoreTurn?:(projectId:string,versionId:string)=>Promise<boolean>,prepareImage?:(file:File)=>Promise<{mime:string,dataUrl:string,width:number,height:number}>}} options
 */
export const initChat = ({ container, requestTool, getProvider, getSummarizer = getProvider, getApi = () => DEFAULT_API, runTurnFn = defaultRunTurn, beforeToolCall, afterToolCall, websearchGranted = false, websearchLog = () => {}, websearchProvider, storage, projectId, getProjectFiles = async () => ({}), getBuild = async () => null, startTurn = () => {}, endTurn = async () => {}, restoreTurn = async () => false, prepareImage = defaultPrepareImage }) => {
  const header = el('div', 'chat-header')
  header.append(el('span', 'chat-title', 'AI Chat'))
  const newChatBtn = el('button', 'chat-new', 'New chat')
  newChatBtn.type = 'button'
  newChatBtn.title = 'Start a new conversation'
  header.append(newChatBtn)
  const messagesEl = el('div', 'chat-messages')
  const form = el('form', 'chat-form')
  const input = el('textarea', 'chat-input')
  input.rows = 2
  input.placeholder = 'Describe the part to build'
  input.autocomplete = 'off'
  const send = el('button', 'chat-send', 'Send')
  const attach = el('button', 'chat-attach')
  attach.type = 'button'
  attach.setAttribute('aria-label', 'Attach images')
  attach.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" d="M21 11.5l-8.5 8.5a5 5 0 0 1-7-7L14 4.5a3.5 3.5 0 0 1 5 5L10.5 18a2 2 0 0 1-3-3L15 7.5"/></svg>'
  const fileInput = el('input', 'chat-attach-input')
  fileInput.type = 'file'
  fileInput.accept = 'image/*'
  fileInput.multiple = true
  fileInput.hidden = true
  form.append(attach, fileInput, input, send)
  const attachNote = el('div', 'chat-attach-note')
  attachNote.setAttribute('role', 'status')
  const pendingEl = el('div', 'chat-pending')
  // Only the phase is live: screen readers hear each change, not each second.
  const statusEl = el('div', 'chat-status')
  const statusPhase = el('span', 'chat-status-phase')
  statusPhase.setAttribute('aria-live', 'polite')
  const statusTime = el('span', 'chat-status-time')
  statusTime.setAttribute('aria-hidden', 'true')
  statusEl.append(statusPhase, statusTime)
  // The recording notice sits directly above the input; the settings word is
  // the link into the gear dialog, which owns the logging toggle.
  const notice = el('div', 'chat-notice')
  const noticeText = el('span', 'chat-notice-text')
  const noticeSettings = el('button', 'chat-notice-settings', 'Chat settings')
  const noticeTail = el('span', 'chat-notice-tail', '.')
  noticeSettings.type = 'button'
  noticeSettings.addEventListener('click', () => document.querySelector('.ai-gear')?.click())
  notice.append(noticeText, noticeSettings, noticeTail)
  container.append(header, messagesEl, notice, attachNote, pendingEl, statusEl, form)

  let running = false
  let undoing = false
  // A Send that is waiting on the consent gate; another Send in the meantime
  // must not queue a second turn.
  let gating = false
  let aborter = null
  // The one open question, if any: its card in the transcript and the promise
  // the turn is awaiting. It is settled exactly once (answer, Stop, a project
  // switch, or the turn ending), so an unanswered question never hangs a turn.
  let pendingQuestion = null
  let status = null
  let startedAt = 0
  let ticker = null
  let assistantEl = null
  // The step's open reasoning block, and the steps this turn has finished.
  let reasoning = null
  let turnReasoning = []
  let turnMentions = []
  // The running turn's user text, handed to `endTurn` so its chat version
  // carries a short excerpt; empty while no turn runs.
  let turnExcerpt = ''
  let transcript = []
  const pid = () => (typeof projectId === 'function' ? projectId() : projectId)
  // Blanket web-search grant for this chat panel: the gear dialog toggles the
  // global default for new conversations, setWebsearchGranted flips this one.
  // Revoking clears the grant, never the stored keys.
  const websearchGrant = createWebsearchGrant({ granted: websearchGranted, log: websearchLog, ask: (input) => ask(input) })
  const gatedBeforeToolCall = async (name, input) =>
    (await websearchGrant.beforeToolCall(name, input)) ?? (await beforeToolCall?.(name, input))
  const gatedAfterToolCall = async (name, input, result) => {
    await websearchGrant.afterToolCall(name, input, result)
    return afterToolCall?.(name, input, result)
  }
  // The running turn's project; set at turn start so module-scope helpers
  // (handleTool) can skip DOM writes once another project is open.
  let turnProject = null
  const live = () => turnProject === pid()
  // opencode and the relay's chat log group a conversation's requests by this
  // id, so it rides on each stored user message and outlives a page reload.
  const sessions = new Map()
  const sessionId = () => {
    const key = pid() ?? ''
    const stored = shown === pid() ? transcript.findLast((m) => typeof m.chatId === 'string')?.chatId : undefined
    if (!sessions.has(key)) sessions.set(key, stored ?? crypto.randomUUID())
    return sessions.get(key)
  }

  const renderAttach = () => {
    const on = visionCapable(getProvider())
    attach.disabled = !on
    attach.title = on ? `Attach images (up to ${MAX_CHAT_IMAGES})` : NO_VISION_TITLE
  }

  // The drawer notice above the input: it reflects the live selection (which
  // `getProvider` reads) and the recording consent, and is re-rendered whenever
  // the gear dialog changes either. Free turns are always saved.
  const renderNotice = () => {
    renderAttach()
    const sel = getProvider()
    // A custom base URL calls the provider directly and is never logged, so the
    // recording line would be wrong there; free turns always record regardless.
    const free = isFreeSelection(sel)
    const show = free || (getRecordOwnChats() && !sel?.baseUrl)
    notice.style.display = show ? '' : 'none'
    if (!show) return
    if (free) {
      noticeText.textContent = CHAT_FREE_NOTICE
      noticeSettings.textContent = ''
      noticeSettings.style.display = 'none'
      noticeTail.textContent = ''
    } else {
      noticeText.textContent = CHAT_LOG_NOTICE.split('Chat settings')[0]
      noticeSettings.textContent = 'Chat settings'
      noticeSettings.style.display = ''
      noticeTail.textContent = '.'
    }
  }

  const persistTranscript = async (id = pid(), messages = transcript) => {
    if (!storage || !id) return
    try {
      await storage.writeConversation(id, forStorage(messages))
    } catch (err) {
      console.warn('chat persist failed:', err)
    }
  }

  const projectFiles = async () => {
    try {
      return (await getProjectFiles()) ?? {}
    } catch (err) {
      console.warn('chat: project files unavailable:', err)
      return {}
    }
  }

  const lastBuild = async () => {
    try {
      return (await getBuild()) ?? null
    } catch (err) {
      console.warn('chat: last build unavailable:', err)
      return null
    }
  }

  const finishTurn = async () => {
    // The turn is over: hand its excerpt to the snapshot once, then clear it so
    // a later no-text turn cannot reuse this one's.
    const excerpt = turnExcerpt
    turnExcerpt = ''
    try {
      const written = await endTurn(excerpt)
      return Array.isArray(written) ? written.filter((w) => w.versionId) : []
    } catch (err) {
      console.warn('chat: saving the turn failed:', err)
      return []
    }
  }

  // A button finds its turn by the version ids: a reload or a storage copy
  // replaces the message object it was drawn for.
  const undoKey = (undo) => JSON.stringify(undo.map(({ projectId, versionId }) => [projectId, versionId]))
  const isTurnOf = (key) => (m) => m.undo?.length > 0 && undoKey(m.undo) === key
  const markUndone = (messages, key) => messages.map((m) => (isTurnOf(key)(m) ? { ...m, undone: true } : m))

  // Only the newest writing turn can be undone: restoring an older one would
  // also drop every later turn's changes. Nothing is undone while a turn or
  // another undo runs.
  const renderUndo = () => {
    const newest = transcript.findLast((m) => m.undo?.length > 0)
    for (const button of messagesEl.querySelectorAll('.chat-undo')) {
      const msg = transcript.find(isTurnOf(button.dataset.undo))
      const undone = msg?.undone === true
      const superseded = !undone && msg !== newest
      button.textContent = undone ? 'Undone' : "Undo this turn's changes"
      button.disabled = undone || superseded || running || undoing
      button.title = superseded ? 'A later turn changed the files' : ''
    }
  }

  // The restore may have reopened projects and redrawn the chat, so the turn
  // is marked in whichever copy of its conversation is current.
  const recordUndone = async (owner, key) => {
    await loading
    if (pid() === owner) {
      transcript = markUndone(transcript, key)
      renderUndo()
      await persistTranscript(owner, transcript)
      return
    }
    if (!storage) return
    try {
      const stored = await storage.readConversation(owner)
      if (stored) await persistTranscript(owner, markUndone(stored.messages, key))
    } catch (err) {
      console.warn('chat persist failed:', err)
    }
  }

  const addUndo = ({ undo }) => {
    const owner = pid()
    const key = undoKey(undo)
    const button = el('button', 'chat-undo')
    button.type = 'button'
    button.dataset.undo = key
    button.addEventListener('click', async () => {
      if (running || undoing) return
      undoing = true
      renderUndo()
      let restored = true
      try {
        for (const { projectId, versionId } of undo) {
          if (!(await restoreTurn(projectId, versionId))) {
            restored = false
            break
          }
        }
      } catch (err) {
        restored = false
        addMessage(`Undo failed: ${err?.message ?? err}`, 'error')
      }
      try {
        if (restored) await recordUndone(owner, key)
      } finally {
        undoing = false
        renderUndo()
      }
    })
    messagesEl.append(button)
    renderUndo()
    follow(true)
  }

  // Streaming must not yank a reader who scrolled up to an earlier reply, so
  // new rows and chunks follow the end only while the reader is already there.
  const NEAR_BOTTOM_PX = 40
  const atBottom = () => messagesEl.scrollHeight - messagesEl.scrollTop - messagesEl.clientHeight <= NEAR_BOTTOM_PX
  const follow = (stick) => { if (stick) messagesEl.scrollTop = messagesEl.scrollHeight }

  const addMessage = (text, kind) => {
    const stick = atBottom()
    const node = el('div', `chat-msg ${kind}`, text)
    messagesEl.append(node)
    follow(stick)
    return node
  }

  const addStopped = () => addMessage('Stopped', 'stopped')

  // A stored image that kept only its type and size draws as a marker.
  const addUserMessage = (text, images = []) => {
    if (images.length === 0) return addMessage(text, 'user')
    const node = addMessage('', 'user')
    if (text) node.append(el('div', 'chat-user-text', text))
    const strip = el('div', 'chat-user-images')
    images.forEach((image, index) => {
      const label = `Your image ${index + 1} of ${images.length}`
      if (typeof image?.dataUrl !== 'string') {
        const gone = el('span', 'chat-user-image-gone', 'image')
        gone.title = `${label} is no longer stored`
        strip.append(gone)
        return
      }
      const thumb = el('button', 'chat-user-thumb')
      thumb.type = 'button'
      thumb.title = `Open full-size ${label.toLowerCase()}`
      thumb.setAttribute('aria-label', `Open full-size ${label.toLowerCase()}`)
      const img = document.createElement('img')
      img.src = image.dataUrl
      img.alt = label
      thumb.append(img)
      thumb.addEventListener('click', () => openViewImage(image.dataUrl, label, thumb))
      strip.append(thumb)
    })
    node.append(strip)
    return node
  }

  const addReasoningBlock = (label, text) => {
    const stick = atBottom()
    const details = el('details', 'chat-reasoning')
    const summary = el('summary', 'chat-reasoning-summary', label)
    const textEl = el('div', 'chat-reasoning-text', text)
    details.append(summary, textEl)
    messagesEl.append(details)
    follow(stick)
    return { summary, textEl }
  }

  const renderLoaded = (messages) => {
    transcript = messages.filter((m) => m.role === 'user' || m.role === 'assistant' || isSummaryMsg(m))
    messagesEl.innerHTML = ''
    assistantEl = null
    turnReasoning = []
    for (const m of transcript) {
      for (const step of m.reasoning ?? []) addReasoningBlock(thoughtLabel(step.seconds), step.text)
      if (m.role === 'user') addUserMessage(m.content, m.images)
      else if (m.role === 'assistant' && m.content) addMessage(m.content, 'assistant')
      if (m.stopped) addStopped()
      if (m.undo?.length > 0) addUndo(m)
    }
    messagesEl.scrollTop = messagesEl.scrollHeight
  }

  // The open project changed: show its conversation, not the previous
  // project's. An in-flight turn keeps its own messages and persists them
  // under the project it started in.
  const showConversation = async (id) => {
    // A question belongs to the project that asked it; leaving dismisses it
    // (a rejection → tool error) so it cannot outlive its project's view.
    settleQuestion((pending) => {
      pending.card.remove()
      pending.reject(new Error('question dismissed by project switch'))
    })
    transcript = []
    messagesEl.innerHTML = ''
    assistantEl = null
    turnReasoning = []
    if (!storage || !id) return
    try {
      const resumed = await storage.readConversation(id)
      if (!resumed || pid() !== id) return
      renderLoaded(resumed.messages)
    } catch (err) {
      console.warn('chat resume failed:', err)
    }
  }

  // Reopening the shown project (an undo, a new file) keeps its view, and with
  // it a running turn's rows; the call still waits for the shown load.
  let shown = null
  let loading = Promise.resolve()
  const switchTo = (id) => {
    if (id === shown) return loading
    shown = id
    loading = showConversation(id)
    return loading
  }

  const newChat = async () => {
    if (running) return
    transcript = []
    messagesEl.innerHTML = ''
    assistantEl = null
    turnReasoning = []
    const id = pid()
    if (id) sessions.delete(id)
    if (storage && id) {
      try {
        await storage.writeConversation(id, [])
      } catch (err) {
        console.warn('chat persist failed:', err)
      }
    }
  }
  newChatBtn.addEventListener('click', newChat)

  const reasoningSeconds = () => Math.floor((Date.now() - reasoning.startedAt) / 1000)

  const addReasoning = (text) => {
    const stick = atBottom()
    if (!reasoning) {
      reasoning = { ...addReasoningBlock(thinkingLabel(0), ''), startedAt: Date.now(), text: '' }
      assistantEl = null
    }
    reasoning.text += text
    reasoning.textEl.textContent += text
    follow(stick)
  }

  const renderReasoning = () => {
    if (reasoning) reasoning.summary.textContent = thinkingLabel(reasoningSeconds())
  }

  // A step's reasoning ends when its text or tool call starts, or the turn ends.
  const endReasoning = () => {
    if (!reasoning) return
    const seconds = Math.max(1, reasoningSeconds())
    reasoning.summary.textContent = thoughtLabel(seconds)
    turnReasoning.push({ text: reasoning.text, seconds })
    reasoning = null
  }

  const renderStatus = () => {
    if (!status) {
      statusEl.classList.remove('active')
      statusPhase.textContent = ''
      statusTime.textContent = ''
      return
    }
    const label = phaseLabel(status)
    if (statusPhase.textContent !== label) statusPhase.textContent = label
    const seconds = Math.floor((Date.now() - startedAt) / 1000)
    const tokens = status.phase === 'thinking' && status.reasoningChars ? `~${Math.round(status.reasoningChars / CHARS_PER_TOKEN)} tokens · ` : ''
    statusTime.textContent = `${tokens}${seconds}s`
    statusEl.classList.add('active')
  }

  // A bare `thinking` is a new provider request, so a new model step.
  const setStatus = (next) => {
    if (next?.phase !== 'thinking' || next.reasoningChars === undefined) endReasoning()
    status = next?.phase === 'done' ? null : next
    renderStatus()
  }

  const tick = () => {
    renderStatus()
    renderReasoning()
  }

  // A full-size view of one agent image, over the app. Backdrop click,
  // the close button or Escape dismisses it; focus returns to the thumbnail.
  const openViewImage = (dataUrl, label, returnTo) => {
    const overlay = el('div', 'chat-view-overlay')
    overlay.setAttribute('role', 'dialog')
    overlay.setAttribute('aria-label', label)
    const full = document.createElement('img')
    full.className = 'chat-view-full'
    full.src = dataUrl
    full.alt = label
    const close = el('button', 'chat-view-close', '✕')
    close.type = 'button'
    close.setAttribute('aria-label', 'Close full-size view')
    overlay.append(full, close)
    const done = () => {
      overlay.remove()
      document.removeEventListener('keydown', onKey, true)
      returnTo?.focus?.()
    }
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        done()
      }
    }
    close.addEventListener('click', (event) => {
      event.stopPropagation()
      done()
    })
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) done()
    })
    document.addEventListener('keydown', onKey, true)
    document.body.append(overlay)
    close.focus?.()
  }

  const addToolLine = (name, inputJson) => {
    const stick = atBottom()
    const details = el('details', 'chat-tool running')
    const summary = el('summary', 'chat-tool-summary')
    summary.append(el('span', 'chat-tool-name', name))
    const target = toolDetail(inputJson)
    if (target !== undefined) summary.append(el('span', 'chat-tool-target', target))
    const mark = el('span', 'chat-tool-mark', '…')
    summary.append(mark)
    details.append(summary)
    details.append(el('pre', 'chat-tool-input', JSON.stringify(inputJson, null, 2)))
    const resultEl = el('pre', 'chat-tool-result', 'running...')
    details.append(resultEl)
    messagesEl.append(details)
    follow(stick)
    return (result, failed, raw) => {
      resultEl.textContent = typeof result === 'string' ? result : JSON.stringify(result, null, 2)
      mark.textContent = failed ? 'failed' : 'ok'
      details.classList.remove('running')
      details.classList.toggle('failed', failed)
      // The `view` tool's pixels travel in `raw.shots[].image.dataUrl`
      // (the displayed text carries cameras only). Show what the agent saw
      // as thumbnails in the same tool row; click opens the full render.
      const shots = Array.isArray(raw?.shots) ? raw.shots.filter((shot) => typeof shot?.image?.dataUrl === 'string') : []
      if (shots.length > 0) {
        const strip = el('div', 'chat-tool-images')
        shots.forEach((shot, index) => {
          const label = `view ${index + 1} of ${shots.length}`
          const thumb = document.createElement('button')
          thumb.type = 'button'
          thumb.className = 'chat-tool-thumb'
          thumb.title = `Open full-size ${label}`
          thumb.setAttribute('aria-label', `Open full-size ${label}`)
          const img = document.createElement('img')
          img.src = shot.image.dataUrl
          img.alt = `Agent ${label}`
          img.loading = 'lazy'
          thumb.append(img)
          thumb.addEventListener('click', () => openViewImage(shot.image.dataUrl, `Agent ${label}`, thumb))
          strip.append(thumb)
        })
        details.append(strip)
        // The row is collapsed by default; an image result opens it so the
        // thumbnails are visible without an extra click.
        details.open = true
      }
    }
  }

  // Settles the one open question, whichever handler fires first. Clearing
  // `pendingQuestion` first makes every other handler a no-op.
  const settleQuestion = (settle) => {
    const pending = pendingQuestion
    if (!pending) return
    pendingQuestion = null
    pending.signal?.removeEventListener('abort', pending.onAbort)
    settle(pending)
  }

  // Answers the open question and marks its card: the chosen option or typed
  // text stays visible where the question was asked.
  const answerQuestion = (value) => {
    const pending = pendingQuestion
    if (!pending) return
    pending.card.classList.add('answered')
    pending.card.append(el('div', 'chat-question-answer', value))
    settleQuestion((open) => open.resolve(value))
  }

  // A running turn's button stops it. As type=button it is no default button,
  // so Enter in the input submits the form, which a running turn ignores.
  const setRunning = (value) => {
    running = value
    send.type = value ? 'button' : 'submit'
    send.textContent = value ? 'Stop' : 'Send'
    send.setAttribute('aria-label', value ? 'Stop the reply' : 'Send the message')
    send.classList.toggle('running', value)
    renderUndo()
    clearInterval(ticker)
    if (value) {
      startedAt = Date.now()
      ticker = setInterval(tick, 1000)
      setStatus({ phase: 'thinking' })
    } else {
      setStatus(null)
      // A turn that ends with a question still open (a timeout, a provider
      // failure) settles it, so the card never outlives its turn.
      settleQuestion((pending) => {
        pending.card.remove()
        pending.reject(abortError('question dismissed'))
      })
    }
  }
  setRunning(false)

  // The abort can end the turn, and turn this back into a submit button, before
  // the click's default action runs, which would then send the draft in the input.
  send.addEventListener('click', (event) => {
    if (!running) return
    event.preventDefault()
    // A draft typed while a question is open is its answer; an empty input
    // means the click is the usual Stop.
    if (pendingQuestion) {
      const answer = input.value.trim()
      if (answer) {
        input.value = ''
        answerQuestion(answer)
        return
      }
    }
    aborter?.abort()
  })

  // Renders the tool line, then hands the result back to the loop: JSON text,
  // except a `view` result, which travels whole so its image parts survive.
  // Never throws: a rejection becomes an {ok:false} result so the turn
  // always has content to feed back.
  const handleTool = async (name, input) => {
    if (typeof input?.path === 'string') turnMentions.push(input.path)
    if (live()) { endReasoning(); assistantEl = null }
    const finish = live() ? addToolLine(name, input) : () => {}
    try {
      const result = await requestTool(name, input)
      finish(forDisplay(result), toolFailed(result), result)
      return forLoop(result)
    } catch (err) {
      const errorResult = { ok: false, error: { name: err.name, message: err.message } }
      finish(errorResult, true)
      return JSON.stringify(errorResult)
    }
  }

  const runTurnLocal = async (message) => {
    const selection = getProvider()
    if (!selection) {
      addMessage('Set your model and API key in Chat settings first.', 'error')
      return
    }
    // Every turn is gated on the accepted terms version and, for a free
    // selection, on the always-recorded notice. Declining blocks this turn with
    // the usual error line and keeps the draft and the history readable.
    const gate = checkTurn(selection)
    if (!gate.ok) {
      if (gating) return
      gating = true
      let allowed = false
      try {
        allowed = await openConsentGate(selection)
      } finally {
        gating = false
      }
      if (!allowed) {
        addMessage(gate.reason === 'free-notice' ? FREE_NOTICE_MESSAGE : TOS_REQUIRED_MESSAGE, 'error')
        return
      }
    }
    const images = pending
    if (images.length > 0 && !visionCapable(selection)) {
      addMessage(NO_VISION_SEND, 'error')
      return
    }
    input.value = ''
    pending = []
    renderPending()
    setAttachNote('')
    turnProject = pid()
    turnExcerpt = message
    renderNotice()
    const chatId = sessionId()
    const turnId = crypto.randomUUID()
    // A custom base URL may be a provider called directly, whose CORS preflight
    // would refuse the log headers. An own-key opt-out must omit them, or the
    // relay would log a turn the user refused.
    const logged = (sel, purpose) => (sel.baseUrl || !getRecordOwnChats() ? {} : { chatId, turnId, ...(purpose ? { purpose } : {}) })
    let local = [...transcript, { role: 'user', content: message, chatId, ...(images.length > 0 ? { images } : {}) }]
    const persist = (messages) => {
      if (!storage || !turnProject) return
      persistTranscript(turnProject, messages)
    }
    // Every phase persists under the project the turn started in; only the
    // visible transcript and the DOM follow the live project.
    const commit = (messages) => {
      local = messages
      persist(messages)
      if (live()) transcript = messages
    }
    commit(local)
    setRunning(true)
    if (live()) addUserMessage(message, images)
    assistantEl = null
    aborter = new AbortController()
    const { signal } = aborter
    let assistantText = ''
    let stopped = false
    let replied = false
    turnReasoning = []
    turnMentions = []
    const reply = (fields) => ({ role: 'assistant', content: assistantText, ...fields, ...(turnMentions.length > 0 ? { mentions: [...turnMentions] } : {}), ...(turnReasoning.length > 0 ? { reasoning: turnReasoning } : {}) })
    try {
      const provider = createProvider({
        ...selection,
        baseUrl: selection.baseUrl || relayBaseUrl(selection),
        sessionId: chatId,
        maxBodyBytes: selection.baseUrl ? undefined : RELAY_BODY_LIMIT_BYTES,
        ...logged(selection),
      })
      startTurn()
      const files = await projectFiles()
      const build = await lastBuild()
      const api = getApi()
      const model = stripSummary(forModelOf(local.slice(0, -1)))
      const prior = local.find(isSummaryMsg)
      // Evict by the same total budget buildMessages sends under, so every
      // trimmed turn lands in the summary instead of vanishing silently.
      const historyBudget = effectiveHistoryBudget({
        systemPrompt: buildSystemPrompt(api),
        transcript: model,
        files,
        build,
        message,
        summary: prior?.text ?? '',
      })
      const { evicted } = partitionTranscript(model, historyBudget)
      const freshTurns = turnsOf(evicted.slice(prior?.basis ?? 0))
      if (freshTurns.length > 0) {
        try {
          const summarizerSelection = getSummarizer()
          const summarizer = createProvider({
            ...summarizerSelection,
            baseUrl: summarizerSelection.baseUrl || relayBaseUrl(summarizerSelection),
            ...logged(summarizerSelection, 'summary'),
          })
          const text = await summarizeTranscript(
            { turns: freshTurns, priorSummary: prior?.text ?? '' },
            summarizeWithProvider(summarizer),
          )
          if (text) {
            const summary = { role: 'system', content: `${SUMMARY_PREFIX}${text}`, text, basis: evicted.length }
            commit([summary, ...stripSummary(local)])
          }
        } catch (err) {
          console.warn('chat summarize failed:', err)
        }
      }
      // A `question` waits on a person, so the loop exempts it from the tool
      // timeout (AWAITS_USER races only the abort signal): an unanswered card
      // outlives any turn-time cap. Every other tool keeps the loop's 120 s
      // default, which is why no toolTimeoutMs is passed here.
      await runTurnFn({
        conversation: { messages: buildMessages({ systemPrompt: buildSystemPrompt(api), transcript: forModelOf(local.slice(0, -1)), files, build, message, images, summary: local.find(isSummaryMsg)?.text }) },
        provider,
        turnId,
        api,
        vision: visionCapable(selection),
        requestTool: handleTool,
        beforeToolCall: gatedBeforeToolCall,
        afterToolCall: gatedAfterToolCall,
        toolFilter:
          typeof websearchProvider === 'function'
            ? (name) => (websearchOff(websearchProvider()) ? name !== 'websearch' : true)
            : websearchOff(websearchProvider)
              ? (name) => name !== 'websearch'
              : undefined,
        onText: (text) => {
          assistantText += text
          if (!live()) return
          const stick = atBottom()
          if (!assistantEl) assistantEl = addMessage('', 'assistant')
          assistantEl.textContent += text
          follow(stick)
        },
        onReasoning: (text) => { if (live()) addReasoning(text) },
        onStatus: (next) => { if (live()) setStatus(next) },
        signal,
      })
      endReasoning()
      if (assistantText) {
        commit([...local, reply()])
        replied = true
      }
    } catch (err) {
      endReasoning()
      if (signal.aborted) stopped = true
      else {
        commit(local.slice(0, -1))
        if (live()) addMessage(inferenceQuotaError(err) ? INFERENCE_QUOTA_MESSAGE : requestTooLarge(err) ? REQUEST_TOO_LARGE_MESSAGE : err.message, 'error')
        // The failed message leaves the transcript, so its images are offered again.
        if (live() && images.length > 0 && pending.length === 0) {
          pending = images
          renderPending()
        }
      }
    } finally {
      if (stopped) {
        if (live()) addStopped()
        commit([...local, reply({ stopped: true })])
        replied = true
      }
      const undo = await finishTurn()
      if (undo.length > 0) {
        commit(replied ? [...local.slice(0, -1), { ...local.at(-1), undo }] : [...local, { role: 'assistant', content: '', undo }])
        if (live()) addUndo(local.at(-1))
      }
      assistantEl = null
      turnProject = null
      aborter = null
      setRunning(false)
    }
  }

  // Images waiting to go out with the next message, and how many are still
  // being downscaled; `attaching` settles once those are ready.
  let pending = []
  let preparing = 0
  let attaching = Promise.resolve()
  let waitingToSend = false

  const setAttachNote = (text = '') => {
    attachNote.textContent = text
    attachNote.classList.toggle('active', text !== '')
  }

  const renderPending = () => {
    pendingEl.replaceChildren(
      ...pending.map((image, index) => {
        const item = el('div', 'chat-pending-item')
        const img = document.createElement('img')
        img.src = image.dataUrl
        img.alt = `Attached image ${index + 1}`
        const remove = el('button', 'chat-pending-remove', '×')
        remove.type = 'button'
        remove.title = 'Remove this image'
        remove.setAttribute('aria-label', `Remove attached image ${index + 1}`)
        remove.addEventListener('click', () => {
          pending = pending.filter((other) => other !== image)
          renderPending()
          input.focus()
        })
        item.append(img, remove)
        return item
      }),
    )
  }

  // Answers whether the files held images, so a paste or drop of them is ours.
  const attachFiles = (files) => {
    const images = imageFiles(files)
    if (images.length === 0) return false
    if (!visionCapable(getProvider())) {
      setAttachNote(NO_VISION_NOTE)
      return true
    }
    const taken = images.slice(0, Math.max(0, MAX_CHAT_IMAGES - pending.length - preparing))
    setAttachNote(taken.length < images.length ? IMAGE_CAP_NOTE : '')
    preparing += taken.length
    const ready = Promise.all(
      taken.map((file) =>
        prepareImage(file).then(
          (image) => image,
          (err) => {
            setAttachNote(err?.message ?? String(err))
            return null
          },
        ),
      ),
    ).then((prepared) => {
      preparing -= taken.length
      pending = [...pending, ...prepared.filter(Boolean)]
      renderPending()
    })
    attaching = Promise.all([attaching, ready])
    return true
  }

  // While a question is open, a submitted draft is its answer, not a new turn.
  form.addEventListener('submit', (event) => {
    event.preventDefault()
    if (pendingQuestion) {
      const answer = input.value.trim()
      if (!answer) return
      input.value = ''
      answerQuestion(answer)
      return
    }
    if (running) return
    // A Send while images are still being prepared goes out once they are.
    if (preparing > 0) {
      if (!waitingToSend) {
        waitingToSend = true
        attaching.finally(() => {
          waitingToSend = false
          form.dispatchEvent(new Event('submit', { cancelable: true }))
        })
      }
      return
    }
    const message = input.value.trim()
    if (!message && pending.length === 0) return
    runTurnLocal(message)
  })

  // The settings' `Model reads images` box changes no selection, so the
  // button is checked again whenever the user comes back to the input.
  input.addEventListener('focus', renderAttach)
  form.addEventListener('pointerenter', renderAttach)

  input.addEventListener('paste', (event) => {
    const data = event.clipboardData
    if (!data || data.getData?.('text/plain')) return
    const files = data.files?.length > 0 ? data.files : [...(data.items ?? [])].filter((item) => item.kind === 'file').map((item) => item.getAsFile())
    if (attachFiles(files)) event.preventDefault()
  })

  attach.addEventListener('click', () => fileInput.click())
  fileInput.addEventListener('change', () => {
    attachFiles(fileInput.files)
    fileInput.value = ''
  })

  // An image dropped on the panel is the chat's; any other drop goes on to the
  // app's project drop on the body.
  const draggingImage = (event) => [...(event.dataTransfer?.items ?? [])].some((item) => item.kind === 'file' && item.type.startsWith('image/'))
  container.addEventListener('dragover', (event) => {
    if (!draggingImage(event)) return
    event.preventDefault()
    event.stopPropagation()
    container.classList.add('chat-dragover')
  })
  container.addEventListener('dragleave', (event) => {
    if (!container.contains(event.relatedTarget)) container.classList.remove('chat-dragover')
  })
  container.addEventListener('drop', (event) => {
    container.classList.remove('chat-dragover')
    if (imageFiles(event.dataTransfer?.files).length === 0) return
    event.preventDefault()
    event.stopPropagation()
    attachFiles(event.dataTransfer.files)
  })

  // A textarea does not submit on Enter; Shift+Enter keeps a newline.
  input.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return
    event.preventDefault()
    form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  })

  // The `question` tool's UI: a card appended to the transcript. With options
  // it is a row of buttons; with none, a small form. The chat input answers it
  // too (above). An abort (Stop) or a project switch rejects it, and the
  // `handleTool` catch turns that into an {ok:false} result — never a hang.
  const ask = (input) => {
    // A turn asks one question at a time; a second call while one is open is a
    // retryable tool error, never a cross-resolution of the open question.
    if (pendingQuestion) return Promise.reject(new Error('answer the open question first'))
    if (typeof input?.question !== 'string' || input.question === '') {
      return Promise.reject(new Error('question: missing question'))
    }
    // A question that arrives after its project was left has no view to live in.
    if (!live()) return Promise.reject(new Error('question dismissed by project switch'))
    return new Promise((resolve, reject) => {
      const stick = atBottom()
      const card = el('div', 'chat-question')
      if (typeof input.header === 'string' && input.header !== '') card.append(el('div', 'chat-question-header', input.header))
      card.append(el('div', 'chat-question-text', input.question))
      const options = Array.isArray(input.options) ? input.options.filter((option) => typeof option === 'string' && option !== '') : []
      if (options.length > 0) {
        const list = el('div', 'chat-question-options')
        for (const option of options) {
          const button = el('button', 'chat-question-option', option)
          button.type = 'button'
          button.addEventListener('click', () => answerQuestion(option))
          list.append(button)
        }
        card.append(list)
      } else {
        const questionForm = el('form', 'chat-question-form')
        const field = el('input', 'chat-question-input')
        field.type = 'text'
        field.placeholder = 'Type your answer'
        const submit = el('button', 'chat-question-submit', 'Answer')
        submit.type = 'submit'
        questionForm.append(field, submit)
        questionForm.addEventListener('submit', (event) => {
          event.preventDefault()
          const value = field.value.trim()
          if (value) answerQuestion(value)
        })
        card.append(questionForm)
      }
      messagesEl.append(card)
      follow(stick)
      const signal = aborter?.signal
      const onAbort = () => {
        card.remove()
        settleQuestion((pending) => pending.reject(abortError()))
      }
      pendingQuestion = { resolve, reject, card, signal, onAbort }
      if (signal) {
        if (signal.aborted) onAbort()
        else signal.addEventListener('abort', onAbort, { once: true })
      }
    })
  }

  if (storage && pid()) switchTo(pid())
  renderNotice()

  return { ask, switchTo, newChat, refreshNotice: renderNotice, setWebsearchGranted: websearchGrant.setGranted, isWebsearchGranted: websearchGrant.isGranted }
}
